/**
 * Quest System Types
 * Flexible, extensible quest system for progression and engagement
 */

import { Quality, FishRarity } from './enums';

/**
 * Quest types
 */
export enum QuestType {
  // Catch quests
  CATCH_SPECIES = 'catch_species',           // Catch X of specific fish
  CATCH_QUANTITY = 'catch_quantity',         // Catch X fish total
  CATCH_QUALITY = 'catch_quality',           // Catch X fish of quality Y
  CATCH_SIZE = 'catch_size',                 // Catch fish above weight/length
  CATCH_RARITY = 'catch_rarity',             // Catch X rare fish

  // Selling quests
  SELL_SPECIES = 'sell_species',             // Sell X of specific fish
  SELL_VALUE = 'sell_value',                 // Earn X gold from selling
  SELL_QUALITY = 'sell_quality',             // Sell X fish of quality Y

  // Progression quests
  EARN_CURRENCY = 'earn_currency',           // Earn X total gold
  SPEND_CURRENCY = 'spend_currency',         // Spend X gold on equipment
  UNLOCK_EQUIPMENT = 'unlock_equipment',     // Buy specific equipment
  UNLOCK_BUDDY = 'unlock_buddy',             // Hire specific buddy

  // Exploration quests
  VISIT_ZONE = 'visit_zone',                 // Discover new zone
  FISH_IN_ZONE = 'fish_in_zone',             // Catch X fish in specific zone

  // Time-based quests
  DAILY_CATCH = 'daily_catch',               // Daily challenge
  TIMED_CHALLENGE = 'timed_challenge',       // Complete in X time

  // Collection quests
  DISCOVER_SPECIES = 'discover_species',     // Catch X different species
  COMPLETE_COLLECTION = 'complete_collection', // Catch all fish in category
}

/**
 * Quest Definition - Static quest data
 */
export interface QuestDefinition {
  id: string;
  type: QuestType;
  name: string;
  description: string;

  // Quest objectives
  objectives: QuestObjective[];

  // Rewards
  rewards: QuestReward[];

  // Unlock requirements
  unlockConditions?: QuestUnlockConditions;

  // Quest properties
  repeatable?: boolean;             // Can be completed multiple times
  dailyReset?: boolean;             // Resets daily (for dailies)
  expiresAt?: number;               // Timestamp when quest expires
  priority?: number;                // Sort order (higher = higher priority)
  isTutorial?: boolean;             // Tutorial/story quest (visually distinct)

  // Visual
  icon?: string;
  category?: string;                // For UI grouping
  giverNpc?: string;                // NPC who gives the quest (for UI display)

  // Quest Chains (organizational)
  chainId?: string;                 // References QuestChain.id
  displayOrder?: number;            // Order within chain (lower = earlier)

  // Chain linking (auto-offer next quest on completion)
  nextQuestId?: string;             // Next quest to offer when this one is turned in
}

/**
 * Quest objective - What player must do
 */
export interface QuestObjective {
  id: string;                       // Unique within quest
  description: string;              // "Catch 10 Bluegill"
  type: QuestObjectiveType;

  // Target criteria
  targetValue: number;              // How many needed
  currentValue?: number;            // Progress (runtime)

  // Filters (what counts toward objective)
  filters?: QuestObjectiveFilters;

  // Optional sub-requirements
  mustBeWild?: boolean;             // Only wild fish count
  minWeight?: number;               // Minimum weight in ounces
  minLength?: number;               // Minimum length in inches
  zoneId?: string;                  // Must be in this zone
}

/**
 * Quest objective types
 */
export enum QuestObjectiveType {
  CATCH_FISH = 'catch_fish',
  SELL_FISH = 'sell_fish',
  EARN_GOLD = 'earn_gold',
  EARN_CURRENCY = 'earn_currency', // Alias for EARN_GOLD
  SPEND_GOLD = 'spend_gold',
  UNLOCK_ITEM = 'unlock_item',
  BUY_EQUIPMENT = 'buy_equipment',
  HIRE_BUDDY = 'hire_buddy',
  VISIT_LOCATION = 'visit_location',
  REACH_MILESTONE = 'reach_milestone',

  // Timing objectives
  TIMING_HIT = 'timing_hit',               // Perfect or Good sweet spot hits

  // Buddy-only objectives (only buddy/hauler actions count)
  BUDDY_CATCH_FISH = 'buddy_catch_fish',   // Only fishing buddy catches
  HAULER_SELL_FISH = 'hauler_sell_fish',   // Only hauler fish sales (count)
  HAULER_EARN_GOLD = 'hauler_earn_gold',   // Only hauler gold earned (value)

  // Trap objectives
  SET_TRAP = 'set_trap',                   // Set a trap X times
  PULL_TRAP = 'pull_trap',                 // Pull a trap X times
  CATCH_CRUSTACEAN = 'catch_crustacean',   // Catch X crustaceans from traps

  // Worm digging objectives
  DIG_WORMS = 'dig_worms',                // Complete worm dig minigame X times
}

/**
 * Filters for quest objectives
 */
export interface QuestObjectiveFilters {
  speciesId?: string | string[];    // Specific fish species
  quality?: Quality | Quality[];    // Required quality
  rarity?: FishRarity | FishRarity[];
  minWeight?: number;               // Minimum weight in ounces
  minLength?: number;               // Minimum length in inches
  zoneId?: string | string[];       // Must be in specific zone(s)
  equipmentId?: string | string[];  // Specific equipment IDs (for BUY_EQUIPMENT)
}

/**
 * Quest rewards
 */
export interface QuestReward {
  type: QuestRewardType;
  value: number | string;           // Amount or ID
  description?: string;             // Display text
}

/**
 * Reward types
 */
export enum QuestRewardType {
  CURRENCY = 'currency',            // Gold reward
  EQUIPMENT = 'equipment',          // Unlock specific equipment
  INVENTORY_EXPANSION = 'inventory_expansion', // +X inventory slots
  UNLOCK_SHOP = 'unlock_shop',      // Unlock new shop
  UNLOCK_ZONE = 'unlock_zone',      // Unlock new fishing zone
  UNLOCK_BUDDY = 'unlock_buddy',    // Unlock buddy slot
  EXPERIENCE = 'experience',        // XP reward (future)
}

/**
 * Quest unlock conditions - VERY FLEXIBLE
 */
export interface QuestUnlockConditions {
  // Player progression
  minLevel?: number;
  minCurrency?: number;
  maxCurrency?: number;             // Only show if player is "poor"

  // Fish caught milestones
  minFishCaught?: number;           // Total fish caught
  minFishCaughtBySpecies?: Record<string, number>; // Species-specific
  minFishCaughtByQuality?: Record<Quality, number>;
  minLargestFishWeight?: number;    // Must have caught fish ≥ X oz
  minLargestFishLength?: number;    // Must have caught fish ≥ X inches

  // Selling milestones
  minFishSold?: number;
  minCurrencyEarned?: number;       // Total gold earned from selling

  // Equipment/upgrades
  ownedEquipment?: string[];        // Must own these equipment IDs
  equippedEquipment?: string[];     // Must have these equipped

  // Buddies
  minBuddyCount?: number;           // Must have X buddies
  unlockedBuddies?: string[];       // Must have specific buddies

  // Zones
  unlockedZones?: string[];         // Must have visited zones
  currentZone?: string;             // Must be in specific zone

  // Quest dependencies
  completedQuests?: string[];       // Must complete these quests first
  activeQuests?: string[];          // Must have these quests active
  failedQuests?: string[];          // Quest becomes available if these failed

  // Time-based
  availableAfter?: number;          // Timestamp - becomes available after
  availableBefore?: number;         // Timestamp - expires if not started

  // Custom condition function (for complex logic)
  customCondition?: string;         // Function name to call for custom validation
}

/**
 * Player quest state (runtime)
 */
export interface PlayerQuestState {
  activeQuests: ActiveQuest[];      // Quests in progress
  completedQuests: string[];        // Quest IDs completed
  failedQuests: string[];           // Quest IDs failed
  availableQuests: string[];        // Quest IDs currently available
  lastRefresh: number;              // Last time quest board refreshed
  /** Saved definitions for accepted procedural quests (survives page refresh) */
  acceptedQuestDefinitions?: Record<string, QuestDefinition>;
}

/**
 * Active quest (runtime instance)
 */
export interface ActiveQuest {
  questId: string;                  // References QuestDefinition
  startedAt: number;                // Timestamp
  expiresAt?: number;               // Timestamp (if timed)

  // Progress tracking
  objectives: QuestObjectiveProgress[];

  // Completion
  completed: boolean;
  completedAt?: number;
  claimedRewards: boolean;
}

/**
 * Quest objective progress
 */
export interface QuestObjectiveProgress {
  objectiveId: string;              // References QuestObjective.id
  currentValue: number;             // Current progress
  targetValue: number;              // Goal
  completed: boolean;
}

/**
 * Quest Chain - Organizational grouping for quests
 * Allows quests to be organized into folders/series
 */
export interface QuestChain {
  id: string;                       // Unique chain ID
  name: string;                     // Display name (e.g., "Fishing Tutorial")
  description?: string;             // Optional description
  color?: string;                   // Badge color (e.g., "#3B82F6")
  icon?: string;                    // Emoji/icon (e.g., "🎓")
  displayOrder: number;             // Order in chain list
  collapsed?: boolean;              // UI state: is chain collapsed?
}
